import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Marker } from '../../models/classes/marker';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { InstructionPopupItem } from '../../models/popup.model';
import { NavButtonComponent } from '../nav-button/nav-button';
import { SelectPhotoPopupComponent } from '../select-photo-popup/select-photo-popup';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { DragMarkerImageComponent } from '../drag-marker/drag-marker';
import { FormsModule } from '@angular/forms';
import { NzImageModule } from 'ng-zorro-antd/image';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { I18N_KEYS } from '../../enums/i18.enum';
import { RotatingEquipment_Type } from '../../enums/global.enum';
import { Images } from '../../enums/icons.enum';

@Component({
  selector: 'app-marker-image',
  templateUrl: './marker-image.html',
  styleUrls: ['./marker-image.scss'],
  imports: [
    NavButtonComponent,
    SelectPhotoPopupComponent,
    DragMarkerImageComponent,
    NzIconModule,
    NzInputModule,
    FormsModule,
    NzImageModule,
  ],
})
export class MarkerImageComponent {
  private ui = inject(UiService);
  public i18n = inject(I18nService);

  // --- Signal Inputs ---
  image = input<string>();
  markers = input<Marker[]>([]);
  useDefaultImage = input<boolean>(false); // Changed to signal as it's modified internally
  editLabel = input<string>();
  editIcon = input<string>();
  dragdrop = input<boolean>();
  defaultImage = input<string>('assets/images/default_image_sensor.png');
  sensorMarkerPlaced = input<boolean>();
  isRotatingEquipment = input<boolean>();
  allowTakePhoto = input<boolean>(true);
  allowUploadPhoto = input<boolean>(true);

  // --- State Signals ---
  showPhotoSelectionPopup = signal(false);
  clientWidth = signal(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  clientHeight = signal(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  imageWidth = signal(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  imageHeight = signal(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);

  // Convert Observable to Signal
  hasCamera = toSignal(this.ui.HasCamera(), { initialValue: false });

  // --- Outputs ---
  addImageClicked = output<void>();
  editImageClicked = output<void>();
  markImageClicked = output<void>();
  useDefaultImageClicked = output<void>();
  imageLoaded = output<any>();
  useRotatingDefaultImageClicked = output<string>();

  // --- Computed Props ---
  isMobile = computed(() => this.ui.IsMobile());

  photoLabel = computed(() => {
    if (this.hasCamera()) {
      if (this.allowTakePhoto() && this.allowUploadPhoto()) {
        return this.i18n.langString(I18N_KEYS.TAKE_OR_UPLOAD_PHOTO);
      }
      if (this.allowTakePhoto()) {
        return this.i18n.langString(I18N_KEYS.TAKE_PHOTO);
      }
    }
    return this.i18n.langString(I18N_KEYS.UPLOAD_PHOTO);
  });

  rotatingEq_itemDetails: InstructionPopupItem[] = [
    {
      id: RotatingEquipment_Type.ROTATINGEQ_CENTRIFUGE,
      title: this.i18n.langString(I18N_KEYS.CENTRIFUGE),
      image: Images.ROTATING_CENTRIFUGE,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_COMPRESOR,
      title: this.i18n.langString(I18N_KEYS.COMPRESSOR),
      image: Images.ROTATING_COMPRESSOR,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_MOTOR,
      title: this.i18n.langString(I18N_KEYS.MOTOR),
      image: Images.ROTATING_MOTOR,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_FAN,
      title: this.i18n.langString(I18N_KEYS.FANS_BLOWERS),
      image: Images.ROTATING_FAN,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_GEARBOX,
      title: this.i18n.langString(I18N_KEYS.GEAR_DRIVERS),
      image: Images.ROTATING_GEAR_BOX,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_PUMP,
      title: this.i18n.langString(I18N_KEYS.PUMP),
      image: Images.ROTATING_PUMP,
    },
    {
      id: RotatingEquipment_Type.ROTATINGEQ_OTHER,
      title: this.i18n.langString(I18N_KEYS.ROTATING_OTHER),
      image: Images.ROTATING_OTHER,
    },
  ];

  onImageLoaded(event: Event) {
    this.imageLoaded.emit(event);
    const el = event.target as HTMLImageElement;
    this.clientWidth.set(el.clientWidth);
    this.clientHeight.set(el.clientHeight);
    this.imageWidth.set(el.naturalWidth);
    this.imageHeight.set(el.naturalHeight);
  }

  saveSelectedPhoto(rotatingEqDefaultImageId: any) {
    this.showPhotoSelectionPopup.set(false);
    this.useRotatingDefaultImageClicked.emit(rotatingEqDefaultImageId);
  }

  loadDefaultImage() {
    this.useDefaultImageClicked.emit();
  }
}
